import React, { useState } from "react"
import { useDispatch } from "react-redux"
import dayjs from "dayjs"
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	TableContainer,
	Stack
} from "@mui/material"
import { COLORS } from "../../utils"
import { downloadSupplierItemRecipesCsv } from "../../actions/ProductionSubstitutions/ProductionSubstitutions"
import SupplierItemRecipesDetailRadio, { SUPPLIER_ITEM_RECIPES_DETAILS } from "./SupplierItemRecipesDetailRadio"

const sx = {
	title: {
		fontSize: 16,
		color: COLORS.BLACK_500,
		fontWeight: 400,
		lineHeight: 1.375,
	},
	headCell: {
		fontWeight: 500,
		whiteSpace: "nowrap"
	},
	dialogActions: {
		display: "flex",
		justifyContent: "space-between",
		pl: 3,
		pr: 3
	}
}

// gross weight in kg, French decimals; empty while the server does not send it yet
const formatGrossWeight = (grossWeight) => typeof grossWeight === "number"
	? grossWeight.toLocaleString("fr-FR", { maximumFractionDigits: 3 })
	: ""

// KFC-598: detail of the recipes in production that contain the replaced supplier item, one row per recipe and production day,
// with the gross weight of the item those portions need
const SupplierItemRecipesDialog = ({ open, onClose, supplierItem, startDate, endDate, supplierItemRecipes }) => {
	const dispatch = useDispatch()
	const [detail, setDetail] = useState(SUPPLIER_ITEM_RECIPES_DETAILS.PRODUCTION_DAY)

	if (!open || !supplierItemRecipes) return null

	const { brands, rows, recipesCount } = supplierItemRecipes

	const handleExport = () => {
		dispatch(downloadSupplierItemRecipesCsv({ supplierItem, startDate, endDate, detail }))
	}

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="supplier-item-recipes-title">
			<DialogTitle id="supplier-item-recipes-title" sx={sx.title}>
				{recipesCount} recette{recipesCount > 1 ? "s" : ""} contenant {supplierItem.name}, prod du {dayjs(startDate).format("DD/MM/YYYY")} au {dayjs(endDate).format("DD/MM/YYYY")}
			</DialogTitle>
			<DialogContent>
				<TableContainer>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={sx.headCell}>Date de prod</TableCell>
								<TableCell sx={sx.headCell}>Réf</TableCell>
								<TableCell sx={sx.headCell}>Produit</TableCell>
								{brands.map(brand => <TableCell key={brand.name} sx={sx.headCell} align="right">{brand.label}</TableCell>)}
								<TableCell sx={sx.headCell} align="right">Total</TableCell>
								<TableCell sx={sx.headCell} align="right">Quantité brute (kg)</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map(row => (
								<TableRow key={row.recipeId + "_" + row.productionDate}>
									<TableCell>{dayjs.utc(row.productionDate).format("DD/MM/YYYY")}</TableCell>
									<TableCell>{row.uniqueCode}</TableCell>
									<TableCell>{row.name}</TableCell>
									{brands.map(brand => <TableCell key={brand.name} align="right">{row.volumes[brand.name]}</TableCell>)}
									<TableCell align="right">{row.total}</TableCell>
									<TableCell align="right">{formatGrossWeight(row.grossWeight)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</DialogContent>
			<DialogActions sx={sx.dialogActions}>
				<Button onClick={onClose} color="primary">
					Fermer
				</Button>
				{/* in the actions, visible however long the table; next to Exporter, the choice it applies to */}
				<Stack direction="row" alignItems="center" gap={4}>
					{rows.length > 0 && <SupplierItemRecipesDetailRadio value={detail} onChange={setDetail} />}
					<Button onClick={handleExport} color="primary" variant="contained" disabled={rows.length === 0}>
						Exporter
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	)
}

export default SupplierItemRecipesDialog
